import { type INPAttribution, onCLS, onFID, onLCP } from "web-vitals";
import { onINP } from "web-vitals/attribution";

import type { DeviceInfo } from "@app/types/device";
import type { ExperimentsVariations } from "@app/types/experiments";
import type { NetworkInformation } from "@app/types/network-information";
import { executeWhenIdle } from "@app/utils/scheduler-yield";

declare global {
  type ClientHint = "model" | "platform" | "platformVersion";
  interface NavigatorUAData {
    getHighEntropyValues<T extends ClientHint[]>(
      keys: T
    ): Promise<{ [K in T[number]]?: string | null }>;
  }
  interface Navigator {
    connection?: NetworkInformation;
    userAgentData?: NavigatorUAData;
  }
}

type WebVitalsMetric = "cls" | "fid" | "lcp" | "inp";

export class WebVitalsReporting {
  private metrics: Record<WebVitalsMetric, number | undefined> = {
    cls: undefined,
    fid: undefined,
    lcp: undefined,
    inp: undefined
  };

  public constructor(
    private request_id: string,
    private experiments: Partial<ExperimentsVariations>,
    private entity: { id: string; type: string } | undefined,
    private locale: string,
    private device: DeviceInfo
  ) {
    onCLS(cls => {
      this.metrics.cls = cls.value;
      this.report("cls");
    });
    onFID(fid => {
      this.metrics.fid = Math.round(fid.value);
      this.report("fid");
    });
    onLCP(lcp => {
      this.metrics.lcp = Math.round(lcp.value);
      this.report("lcp");
    });
    onINP(inp => {
      this.metrics.inp = Math.round(inp.value);
      this.report("inp", inp.attribution);
    });
  }

  private async report(sample: WebVitalsMetric, attribution?: INPAttribution) {
    const navigator = window.navigator;

    const { model, platform, platformVersion } = await (
      navigator?.userAgentData?.getHighEntropyValues([
        "model",
        "platform",
        "platformVersion"
      ]) ?? Promise.resolve({ model: "", platform: "", platformVersion: "" })
    ).catch(() => ({ model: "", platform: "", platformVersion: "" }));

    // Some web vitals reports can be triggered on user interaction
    // leading to a decrease in INP performance.
    // Hence, the setTimeout, to ensure the report computation is done asynchronously
    executeWhenIdle(() => {
      const request_id = this.request_id;
      const entity = this.entity;
      const experiments = this.experiments;
      const locale = this.locale;

      let url = "/i?requestId=" + encodeURIComponent(request_id);

      try {
        const props: Record<
          string,
          string | number | boolean | undefined | null
        > = {
          sample: sample,
          entityId: (entity && entity.id) || "",
          entityType: (entity && entity.type) || "",
          totalLongTasksDuration: -1,
          locale,
          experiments: "",

          // Connection quality
          connectionType: navigator?.connection?.effectiveType,
          downlink: navigator?.connection?.downlink,
          saveData: navigator?.connection?.saveData,

          // User information
          deviceModel: model || this.device.model,
          devicePlatform: platform
            ? `${platform} ${platformVersion || ""}`
            : this.device.os,

          // Web Vitals
          interactionToNextPaint: this.metrics.inp,
          cumulativeLayoutShift: this.metrics.cls,
          firstInputDelay: this.metrics.fid,
          largestContentfulPaint: this.metrics.lcp,

          ...(attribution
            ? {
                eventTarget: attribution.interactionTarget,
                eventTime: attribution.interactionTime,
                eventType: attribution.interactionType,
                loadState: attribution.loadState
              }
            : null)
        };

        if (window.performance && window.performance.getEntriesByType) {
          // Generally Chrome
          const nagivation_entries =
            window.performance.getEntriesByType("navigation");
          const entry = nagivation_entries && nagivation_entries[0];

          if (entry) {
            (
              Object.keys(
                entry.toJSON()
              ) as (keyof PerformanceNavigationTiming)[]
            ).forEach(function (k) {
              const value = entry[k];
              if (
                !value ||
                typeof value === "number" ||
                typeof value === "string"
              ) {
                props[k] =
                  value && typeof value === "number" && value.toFixed
                    ? value.toFixed(0)
                    : value;
              }
            });
          }

          const paint_entries = window.performance.getEntriesByType("paint");

          if (paint_entries) {
            paint_entries.forEach(function (e) {
              if (e.startTime) {
                // We'll get event names like `first-paint` and `first-contentful-paint`
                props[e.name] = e.startTime.toFixed(0);
              }
            });
          }

          const resource_entries =
            window.performance.getEntriesByType("resource");

          // Third party domains need to send the [Timing Allow Origin](https://developer.mozilla.org/en-US/docs/Web/HTTP/Headers/Timing-Allow-Origin) header
          // for use to know the size of their resources.
          // It means that this total_transfer_size is not accurate, as it represents only the size of the resources from our domain
          // + size of resources of some third party domains that have activated the headers.
          const total_transfer_size =
            resource_entries.reduce((acc, p) => acc + p.transferSize, 0) +
            nagivation_entries.reduce((acc, p) => acc + p.transferSize, 0);

          props["totalTransferSize"] = total_transfer_size;
        } else {
          // Safari, Firefox, Edge, IE and Opera
          const perf_timing = window.performance && window.performance.timing;
          (
            Object.keys(perf_timing.toJSON()) as (keyof PerformanceTiming)[]
          ).forEach(function (k) {
            const perf_timing_value = perf_timing[k];
            if (typeof perf_timing_value !== "number") {
              return;
            }
            props[k] =
              perf_timing_value === 0
                ? 0
                : perf_timing_value - perf_timing.navigationStart;
          }, "");
        }

        /**
         * Priority is to track the performance metrics.
         *
         * Under some conditions (old browser, lots of xps), we'll exceed the URL length a browser is capable of requesting,
         * ref https://stackoverflow.com/questions/417142/what-is-the-maximum-length-of-a-url-in-different-browsers.
         *
         * Whenever we exceed the length of the submitted URL, we just want to be in a position to detect it before we log
         * it on the Fastly side to prevent data corruption and dropping all the inbound logs. To that end, we prefix the
         * payload with an expected length and encode into base64. On Fastly, we can decode, check the length and if the
         * payload and expected length match, consider the log valid for insertion.
         *
         * Note that base64 encoding will prevent the URL unsafe characters []{},:" from getting escaped, at a cost
         * representing each character using more bytes. This is usually a worthwhile tradeoff as shown below using
         * a 13 experiment set
         *
         * > xps.length
         * > 13
         * > JSON.stringify(xps).length
         * > 508
         * > window.btoa(JSON.stringify(xps)).length
         * > 680
         * > encodeURIComponent(JSON.stringify(xps)).length
         * > 874
         *
         * Since `control` is a very likely occurence (typically 50% per XP), and most XPs will represent other
         * variants using a single character, we have an opportunity for compression through a simple replacement
         * which we can reverse on Fastly
         *
         * > JSON.stringify(xps.map(({k,v}) => ({k, v:v.replace('control','#')}))).length
         * > 430
         * > window.btoa(JSON.stringify(xps.map(({k,v}) => ({k, v:v.replace('control','#')})))).length
         * > 576
         * > encodeURIComponent(JSON.stringify(xps.map(({k,v}) => ({k, v:v.replace('control','#')})))).length
         * > 822
         */
        if (experiments && typeof window.btoa !== "undefined") {
          // If the browser does not support btoa, it is ancient, and we'll discard the experiments component of the querystring
          const xps = JSON.stringify(
            Object.keys(experiments).map(function (k) {
              return { k: k, v: experiments[k]?.replace("control", "#") };
            })
          );

          props.experiments = btoa(xps.length + xps);
        }

        // Serialize querystring, and place experiments at end in case of URL truncation
        url += Object.keys(props)
          .filter(k => k !== "experiments")
          .sort()
          .concat("experiments")
          .reduce(function (qs, k) {
            const prop_value = props[k];
            if (prop_value === undefined) {
              return qs;
            }
            return (
              qs +
              "&" +
              encodeURIComponent(k) +
              "=" +
              encodeURIComponent(prop_value === null ? "null" : prop_value)
            );
          }, "");
      } catch (ex) {
        url +=
          "&error=" +
          encodeURIComponent(ex && (ex.message || ex)).slice(0, 200);
      }

      if (navigator && "sendBeacon" in navigator) {
        const beaconSent = window.navigator.sendBeacon(url);
        if (beaconSent) {
          return;
        }
      }
      // Fallback to old trick
      new Image(1, 1).src = url;
    });
  }
}
